import { motion } from "framer-motion";
import { Waves, Users, BookOpen, Fence, LifeBuoy, HeartPulse, Home, HeartHandshake } from "lucide-react";

const LAYERS = [
  ["Swim Lessons", Waves, "#00B4D8"],
  ["Adult Supervision", Users, "#0077B6"],
  ["Water-Safety Education", BookOpen, "#FF6B35"],
  ["Fences & Barriers", Fence, "#03045E"],
  ["Life Jackets", LifeBuoy, "#10B981"],
  ["CPR & Emergency Preparedness", HeartPulse, "#EF4444"],
  ["Safe Communities", Home, "#8B5CF6"],
  ["Engaged Families", HeartHandshake, "#F59E0B"],
];

export default function CircleOfProtection({ compact = false }) {
  const size = compact ? 340 : 460;
  const small = typeof window !== "undefined" && window.innerWidth < 400;
  const r = size * 0.34;
  if (small) {
    return (
      <div className="mx-auto w-full" data-testid="circle-of-protection">
        <div className="mx-auto w-40 h-40 rounded-full bg-gradient-to-b from-sky-100 to-white shadow-[0_0_40px_rgba(0,180,216,0.35)] border-2 border-dashed border-sky-300 flex flex-col items-center justify-center">
          <img src="/brand/logo.webp" alt="Child at the center" className="w-[70%] floaty" />
          <p className="font-display uppercase text-[#03045E] text-xs font-bold">The Child</p>
        </div>
        <p className="text-center font-display uppercase text-xs tracking-[0.2em] text-[#0077B6] mt-3">Circle of Protection™</p>
        <div className="grid grid-cols-2 gap-2 mt-4">
          {LAYERS.map(([label, Icon, color], i) => (
            <div key={label} data-testid={`circle-layer-${i}`} className="flex items-center gap-2 rounded-xl bg-white border border-sky-100 p-2">
              <span className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-white" style={{ background: color }}><Icon className="w-4 h-4" /></span>
              <span className="text-[11px] font-bold uppercase leading-tight text-[#03045E]">{label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="relative mx-auto w-full aspect-square" style={{ maxWidth: size }} data-testid="circle-of-protection">
      <svg className="absolute inset-0 w-full h-full spin-slow" viewBox="0 0 460 460" aria-hidden>
        <defs><path id="ring" d="M230,230 m-212,0 a212,212 0 1,1 424,0 a212,212 0 1,1 -424,0" /></defs>
        <text fontFamily="Oswald" fontSize="15" letterSpacing="5" fill="#0077B6" fontWeight="600">
          <textPath href="#ring">SWIM FOR RAKIM™ CIRCLE OF PROTECTION™ • NO SINGLE LAYER IS ENOUGH •</textPath>
        </text>
      </svg>
      <div className="absolute inset-[13%] rounded-full border-2 border-dashed border-sky-300" />
      <div className="absolute inset-[30%] rounded-full bg-gradient-to-b from-sky-100 to-white shadow-[0_0_40px_rgba(0,180,216,0.35)] flex flex-col items-center justify-center text-center">
        <img src="/brand/logo.webp" alt="Child at the center" className="w-[62%] floaty" />
        <p className="font-display uppercase text-[#03045E] text-sm font-bold -mt-1">The Child</p>
      </div>
      {LAYERS.map(([label, Icon, color], i) => {
        const a = (i / LAYERS.length) * Math.PI * 2 - Math.PI / 2;
        const x = size / 2 + r * Math.cos(a);
        const y = size / 2 + r * Math.sin(a);
        return (
          <motion.div key={label} data-testid={`circle-layer-${i}`} initial={{ opacity: 0, scale: 0.5 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
            className="absolute flex flex-col items-center w-16 sm:w-24 -translate-x-1/2 -translate-y-1/2 text-center" style={{ left: `${(x / size) * 100}%`, top: `${(y / size) * 100}%` }}>
            <span className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white shadow-lg ring-4 ring-white" style={{ background: color }}><Icon className="w-5 h-5" /></span>
            <span className="mt-1 text-[9px] sm:text-[11px] font-bold uppercase leading-tight text-[#03045E] bg-white/80 rounded px-1">{label}</span>
          </motion.div>
        );
      })}
    </div>
  );
}
